import { create } from 'zustand';

// Notification types and their display settings
const NOTIFICATION_CONFIG = {
  trade_executed: { 
    icon: 'check', 
    color: 'emerald', 
    autoHide: false, // Require manual dismiss
    sound: true,
    groupKey: (n) => `trade-${n.market?.conditionId || 'unknown'}`
  },
  trade_failed: { 
    icon: 'x', 
    color: 'red', 
    autoHide: false,
    sound: true,
    groupKey: (n) => `trade-fail-${n.market?.conditionId || 'unknown'}`
  },
  position_opened: { 
    icon: 'plus', 
    color: 'primary', 
    autoHide: false,
    sound: false,
    groupKey: (n) => `pos-open-${n.conditionId || 'unknown'}`
  },
  position_closed: { 
    icon: 'check', 
    color: 'emerald', 
    autoHide: false,
    sound: false,
    groupKey: (n) => `pos-close-${n.conditionId || 'unknown'}`
  },
  position_stopped: { 
    icon: 'alert', 
    color: 'amber', 
    autoHide: false,
    sound: true,
    groupKey: (n) => `pos-stop-${n.conditionId || 'unknown'}`
  },
  error: { 
    icon: 'alert', 
    color: 'red', 
    autoHide: false,
    sound: false,
    groupKey: (n) => `error-${n.code || 'generic'}`
  },
  info: { 
    icon: 'info', 
    color: 'primary', 
    autoHide: true,
    autoHideDelay: 5000,
    sound: false,
    groupKey: () => null // Don't group info notifications
  }
};

// Spam prevention: track recent notifications to avoid duplicates
const SPAM_WINDOW_MS = 5000; // 5 seconds
const MAX_VISIBLE = 5; // Max toasts visible at once

export const useNotificationStore = create((set, get) => ({
  notifications: [], // Active toast notifications
  history: [], // All notifications (for the notification panel)
  recentKeys: new Map(), // For spam prevention: groupKey -> timestamp
  unreadCount: 0,
  
  // Add a new notification
  addNotification: (notification) => {
    const { notifications, recentKeys, history } = get();
    
    const type = notification.type || 'info';
    const config = NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.info;
    
    // Generate group key for spam prevention
    const groupKey = config.groupKey ? config.groupKey(notification) : null;
    
    // Check for spam (same notification within window)
    if (groupKey) {
      const lastTime = recentKeys.get(groupKey);
      if (lastTime && Date.now() - lastTime < SPAM_WINDOW_MS) {
        // Skip duplicate notification
        return;
      }
      recentKeys.set(groupKey, Date.now());
    }
    
    const id = notification.id || `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    
    const newNotification = {
      id,
      type,
      title: notification.title || 'Notification',
      message: notification.message || '',
      details: notification.details || null, // For linking to detailed view
      market: notification.market || null,
      createdAt: notification.createdAt || new Date().toISOString(),
      read: false,
      config
    };
    
    // Add to active toasts (limit visible count)
    const updatedNotifications = [newNotification, ...notifications].slice(0, MAX_VISIBLE);
    
    // Add to history
    const updatedHistory = [newNotification, ...history].slice(0, 100);
    
    set({ 
      notifications: updatedNotifications,
      history: updatedHistory,
      unreadCount: get().unreadCount + 1
    });
    
    // Auto-hide if configured
    if (config.autoHide && config.autoHideDelay) {
      setTimeout(() => {
        get().dismissNotification(id);
      }, config.autoHideDelay);
    }
    
    // Clean up old spam prevention keys
    const now = Date.now();
    for (const [key, time] of recentKeys.entries()) {
      if (now - time > SPAM_WINDOW_MS * 2) {
        recentKeys.delete(key);
      }
    }
  },
  
  // Dismiss a specific notification (remove from toasts)
  dismissNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter(n => n.id !== id),
      history: state.history.map(n => n.id === id ? { ...n, read: true } : n),
      unreadCount: Math.max(0, state.unreadCount - 1)
    }));
  },
  
  // Dismiss all visible toasts
  dismissAll: () => {
    set((state) => ({
      notifications: [],
      history: state.history.map(n => ({ ...n, read: true })),
      unreadCount: 0
    }));
  },
  
  // Mark all as read (for notification panel)
  markAllRead: () => {
    set((state) => ({
      history: state.history.map(n => ({ ...n, read: true })),
      unreadCount: 0
    }));
  },
  
  // Clear history
  clearHistory: () => {
    set({ history: [], unreadCount: 0 });
  }
}));

// Helper to create trade notifications
export function createTradeNotification(trade, type = 'trade_executed') {
  const isBuy = trade.side === 'BUY';
  const size = trade.filledSize?.toFixed(2) || trade.requestedSize?.toFixed(2) || '?';
  const price = trade.avgFillPrice?.toFixed(3) || '?';
  const market = trade.market?.question?.slice(0, 50) || 'Unknown Market';
  
  if (type === 'trade_executed') {
    return {
      type: 'trade_executed',
      title: `${isBuy ? 'Bought' : 'Sold'} ${size} shares`,
      message: `${market}${market.length >= 50 ? '...' : ''} @ $${price}`,
      market: trade.market,
      details: { tradeId: trade._id, configId: trade.copyConfigId }
    };
  } else {
    return {
      type: 'trade_failed',
      title: `Trade Failed: ${trade.executionDetails?.failureCode || 'Error'}`,
      message: `${market}${market.length >= 50 ? '...' : ''}`,
      market: trade.market,
      details: { tradeId: trade._id, configId: trade.copyConfigId, error: trade.errorMessage }
    };
  }
}

// Helper to create position notifications
export function createPositionNotification(position, status) {
  const market = position.market?.question?.slice(0, 50) || 'Unknown Market';
  const pnl = position.realizedPnL || position.unrealizedPnL || 0;
  const pnlStr = pnl >= 0 ? `+$${pnl.toFixed(2)}` : `-$${Math.abs(pnl).toFixed(2)}`;
  
  const typeMap = {
    'open': 'position_opened',
    'closed': 'position_closed',
    'stopped_out': 'position_stopped',
    'take_profit': 'position_closed'
  };
  
  const titleMap = {
    'open': 'Position Opened',
    'closed': `Position Closed (${pnlStr})`,
    'stopped_out': `Stop Loss Triggered (${pnlStr})`,
    'take_profit': `Take Profit Hit (${pnlStr})`
  };
  
  return {
    type: typeMap[status] || 'info',
    title: titleMap[status] || 'Position Updated',
    message: `${market}${market.length >= 50 ? '...' : ''}`,
    conditionId: position.market?.conditionId,
    details: { positionId: position._id }
  };
}
